import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ReactDOM from "react-dom/client";
import domtoimage from "dom-to-image";
import { Document, Packer, Paragraph, TextRun } from "docx";
import AllocationEqualizer from './summary/AllocationEqualizer';
import SprintCard from './summary/SprintCard';
import Summary from './summary/Summary';
import './styles/ProjectSummary.css';
import ProductHeader from "../components/ProductHeader";
import RichTextEditor from '../utils/RichTextEditor';
import ResourceAllocationReport from './summary/ResourceAllocationReport';
import ResourceAllocationGrid from './summary/ResourceAllocationGrid';
import { format, addDays, isWeekend, isSameDay, parse, isValid } from 'date-fns';
import { updatedResourceAllocationGrid } from '../utils/allocationHelpers';
import EstimatedAllocation from './summary/EstimatedAllocation';

import {
    normalizeDate,
    isBusinessDay,
    getPreviousBusinessDay,
    getNextBusinessDay,
    calculateWorkingDays,
    parseDate,
    formatCurrency,
    formatHours,
    formatDate
} from '../utils/helpers';



const ProjectSummary = ({ formData, setFormData, onBackToReview, navigateToPage, saveJsonFile, saveJsonFileFallback, isSubscribed }) => {
    const [allocations, setAllocations] = useState([]);
    const [weekendDays, setWeekendDays] = useState(formData.weekendDays || [0, 6]);
    const [holidays, setHolidays] = useState(formData.holidays || []);
    const [highlightedCells, setHighlightedCells] = useState(new Set());
    useEffect(() => {
        setFormData(prevData => ({
            ...prevData,
            currentPage: "summary"
            // 🔹 Set this to the correct page name
        }));
    }, []);

    useEffect(() => {
        if (!isSubscribed) {
            navigateToPage('home');  // Redirect to Home if not subscribed
            return;
        }

        

        // if (formData.preferredResources.length === 1 || formData.sprintsMilestones.length === 1) {
        //     navigateToPage('dashboard'); // Redirect to Dashboard if form data is missing
        // }
    }, [formData, isSubscribed, navigateToPage]);

    useEffect(() => {
        setWeekendDays(formData.weekendDays || [0, 6]);
        setHolidays(formData.holidays || []);
    }, [formData]);

    useEffect(() => {
        setFormData(prevData => ({
            ...prevData,
            termsAndConditions: prevData.termsAndConditions || { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text: "" }] }] },
            estimatePreparedBy: prevData.estimatePreparedBy || { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text: "" }] }] },
            estimatePreparedFor: prevData.estimatePreparedFor || { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text: "" }] }] },
        }));
    }, []);


    useEffect(() => {
        if (!formData.startDate) {
            return;
        }

        const updatedSprints = calculateSprintDates(formData);

        if (updatedSprints.length > 0) {
            setFormData(prevData => ({
                ...prevData,
                sprintsMilestones: updatedSprints
            }));
        }
    }, [formData.startDate, formData.weekendDays, formData.holidays]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handleRestrictedInputChange = (e) => {
        const { name, value } = e.target;
        let numericValue = parseInt(value, 10);

        if (isNaN(numericValue)) numericValue = 1; // Ensure min value is 1
        if (numericValue > 24) numericValue = 24; // Ensure max value is 24

        handleInputChange({ target: { name, value: numericValue } });
    };

    const handleWeekendChange = (e) => {
        const { value, checked } = e.target;
        const updatedWeekendDays = checked
            ? [...weekendDays, parseInt(value)]
            : weekendDays.filter((day) => day !== parseInt(value));

        setWeekendDays(updatedWeekendDays);
        setFormData(prevData => ({ ...prevData, weekendDays: updatedWeekendDays }));
    };

    const handleHolidayChange = (date) => {
        if (!holidays.includes(date)) {
            const updatedHolidays = [...holidays, date];
            setHolidays(updatedHolidays);
            setFormData(prevData => ({ ...prevData, holidays: updatedHolidays }));
        }
    };

    const removeHoliday = (dateToRemove) => {
        const updatedHolidays = holidays.filter(date => date !== dateToRemove);
        setHolidays(updatedHolidays);
        setFormData(prevData => ({
            ...prevData,
            holidays: updatedHolidays,
            sprintsMilestones: calculateSprintDates({ ...prevData, holidays: updatedHolidays })
        }));
    };

    const validateFormData = () => {
        const requiredFields = ["projectName", "startDate", "budget", "estimatedHours", "preferredResources", "sprintsMilestones"];
        const missingFields = requiredFields.filter(field => !formData[field] || (Array.isArray(formData[field]) && formData[field].length === 0));

        if (missingFields.length > 0) {
            alert(`Please fill in the required fields before proceeding:\n\n${missingFields.join(", ")}`);
            return false;
        }
        return true;
    };

    const calculateSprintDates = (formData) => {
        if (!formData.startDate || !formData.sprintsMilestones) {
            return [];
        }

        let currentDate = parseDate(formData.startDate);
        const weekends = formData.weekendDays || [0, 6];
        const holidays = formData.holidays.map(date => parseDate(date));

        const updatedSprints = formData.sprintsMilestones.map((sprint) => {
            let plannedStart = new Date(currentDate);
            let plannedEnd = addDays(plannedStart, (sprint.duration * 7) - 1);
            let sprintHolidays = holidays.filter(holiday => holiday >= plannedStart && holiday <= plannedEnd);

            const allDaysAreHolidays = sprintHolidays.length === (plannedEnd - plannedStart) / (1000 * 60 * 60 * 24) + 1;

            let actualStart = null;
            let actualEnd = null;
            let workingDays = 0;

            if (!allDaysAreHolidays) {
                actualStart = isBusinessDay(plannedStart, weekends, holidays)
                    ? new Date(plannedStart)
                    : getNextBusinessDay(plannedStart, weekends, holidays);

                actualEnd = isBusinessDay(plannedEnd, weekends, holidays)
                    ? new Date(plannedEnd)
                    : getPreviousBusinessDay(plannedEnd, weekends, holidays);

                workingDays = calculateWorkingDays(actualStart, actualEnd, weekends, holidays);
            }

            let nextSprintStart = addDays(plannedEnd, 1);
            if (!isBusinessDay(nextSprintStart, weekends, holidays)) {
                nextSprintStart = getNextBusinessDay(nextSprintStart, weekends, holidays);
            }

            currentDate = new Date(nextSprintStart);

            return {
                ...sprint,
                startDate: format(plannedStart, 'yyyy-MM-dd'),
                actualStartDate: actualStart ? format(actualStart, 'yyyy-MM-dd') : null,
                endDate: format(plannedEnd, 'yyyy-MM-dd'),
                actualEndDate: actualEnd ? format(actualEnd, 'yyyy-MM-dd') : null,
                workingDays,
                holidays: sprintHolidays.map(date => format(date, 'yyyy-MM-dd'))
            };
        });

        return updatedSprints;
    };

    const handleGenerateResourceAllocation = () => {
        if (!validateFormData()) return;

        setFormData(prevData => {
            const updatedGrid = updatedResourceAllocationGrid(prevData);

            return {
                ...prevData,
                resourceAllocationGrid: updatedGrid
            };
        });
    };

    const handleAllocationChange = (sprintIndex, resourceId) => {
        setHighlightedCells(new Set([`${sprintIndex}-${resourceId}`])); // ✅ Resets to only the current change

        setHighlightedCells(prev => {
            //console.log(`✅ Highlighting Cell: Sprint ${sprintIndex}, Resource ${resourceId}`);
            return new Set([...prev, `${sprintIndex}-${resourceId}`]);
        });
    };

    const handleDownloadAsPDF = () => {
        const element = document.getElementById("project-summary");

        if (!element) {
            alert("Project summary content is missing.");
            return;
        }

        html2canvas(element, { scale: 2 }).then((canvas) => {
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");
            const imgWidth = 210;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
            pdf.save("Project_Summary.pdf");
        });
    };

    //handleDownloadAsPDF
    const openPopup = (formData = {}, allocations = []) => {
        if (!formData || typeof formData !== "object") {
            console.error("Error: formData is missing or invalid!", formData);
            alert("Error: Missing project data!");
            return;
        }
    
        if (!Array.isArray(allocations)) {
            console.error("Error: allocations is not an array!", allocations);
            allocations = []; // Provide an empty array
        }
    
        // Open the popup
        const popup = window.open("", "_blank", "width=900,height=700");
    
        const today = new Date().toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    
        // Extract values safely from formData before writing to popup
        const extractText = (html) => {
            if (!html) return "N/A"; // Handle empty or undefined input
    
            // Create a DOM parser
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
    
            // Extract text content
            return doc.body.textContent || "N/A";
        };
    
        //console.log("termsAndConditions", formData.termsAndConditions);
        // Extract values safely from formData
        const termsAndConditions = extractText(formData.termsAndConditions);
        const preparedBy = extractText(formData.estimatePreparedBy);
        const preparedFor = extractText(formData.estimatePreparedFor);
    
        if (!popup) {
            alert("Popup blocked! Please allow popups for this site.");
            return;
        }
    
        popup.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Project Summary</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    .container { max-width: 800px; margin: auto; }
                    .popup-header { font-size: 20px; font-weight: bold; margin-bottom: 15px; }
                    .print-button {
                        padding: 10px 15px;
                        font-size: 14px;
                        background-color: #007bff;
                        color: white;
                        border: none;
                        border-radius: 5px;
                        cursor: pointer;
                        margin-top: 15px;
                    }
    
                    /* Hide the Print button when printing */
                    @media print {
                        .print-button {
                            display: none;
                        }
                    }
                </style>
            </head>
            <body>
                <div id="popup-root" class="container"></div>
            </body>
            </html>
        `);
        popup.document.close();
    
        popup.onload = function () {
            const popupRoot = popup.document.getElementById("popup-root");
            const root = ReactDOM.createRoot(popupRoot);
            if (!popupRoot) {
                console.error("Popup root not found!");
                return;
            }
    
            root.render(
                <div>
                    {/* <h2 className="popup-header">📊 Project Summary</h2> */}
                    <div id="summary-section">
                        <Summary formData={formData} allocations={allocations} />
                    </div>
                    <div id="estimated-allocation-section">
                        <EstimatedAllocation formData={formData} />
                    </div>
                    <div className="section-title"><strong>Terms and Conditions:</strong></div>
                    <div className="content-box">
                        {termsAndConditions}
                    </div>
                    <div className="section-title"><strong>Prepared By:</strong></div>
                    <div className="content-box">
                        {preparedBy}
                    </div>
    
                    <div className="section-title"><strong>Prepared For:</strong></div>
                    <div className="content-box">
                        {preparedFor}
                    </div>
    
                    <p><strong>Date:</strong> {today}</p>
    
                    {/* ✅ Move Print Button inside React Rendered Content */}
                    <button className="print-button" onClick={() => popup.print()}>🖨 Print / Save as PDF</button>
                </div>
            );
        };
    };
    

    const handleDownloadSprintDetailsForJira = () => {
        if (!validateFormData()) return;

        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += "Sprint Name,Start Date,End Date,Working Start Date,Working End Date,Working Days,Role,Allocated Hours,Issue Type,Summary,Description,Assignee,Labels,Priority,Status\n";

        formData.resourceAllocationGrid.forEach(({ sprint, allocations }) => {
            Object.entries(allocations).forEach(([resourceId, allocation]) => {
                const resource = allocation.resource;

                csvContent += `${sprint.name},${sprint.startDate},${sprint.endDate},${sprint.actualStartDate},${sprint.actualEndDate},${sprint.workingDays},${resource.role},${allocation.allocatedHours},Task,${sprint.name} Task,Details for ${sprint.name},Unassigned,Sprint-${sprint.name},Medium,To Do\n`;
            });
        });

        // Encode and trigger download
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${formData.projectName}_Jira_Sprint_Details.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };




    return (
        <div className="container">
            <div className="summary-container">
                <ProductHeader navigateToPage={navigateToPage} formData={formData} />
                <br></br>
                <div className="project-summary-content">
                    <h2 className="subtitle">Project Estimation Overview</h2>

                    <p className="description">
                        The <strong>{formData.projectName}</strong> project is set to begin on
                        <input
                            type="date"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleInputChange}
                            className="inline-input input-field input-date"
                        />, with a budget of
                        <input
                            type="number"
                            name="budget"
                            value={formData.budget}
                            onChange={handleInputChange}
                            className="inline-input input-field input-budget"
                        /> dollars and an estimated workload of
                        <input
                            type="number"
                            name="estimatedHours"
                            value={formData.estimatedHours}
                            onChange={handleInputChange}
                            className="inline-input input-field input-hours"
                        /> hours. This project will follow the <strong>{formData.implementationType}</strong> methodology, focusing on flexibility and iterative progress. The team will consist <strong>
                            {formData.preferredResources.length > 0
                                ? formData.preferredResources.map(res => `${res.name}`).join(', ')
                                : 'None selected'}
                        </strong> as key resources.
                    </p>

                    <p className="description">
                        The project will be delivered in the following {formData.implementationType === 'Agile' ? 'sprints' : 'milestones'}:
                    </p>

                    <ul className="milestone-list">
                        {formData.sprintsMilestones.map((milestone, index) => (
                            <li key={index} className="milestone-item">
                                {milestone.name} – {milestone.duration} weeks
                            </li>
                        ))}
                    </ul>
                    <br></br>

                    {/* Weekend Selection UI */}
                    <div className="weekend-holiday-container">
                        <div className="weekend-selection">
                            <label className="weekend-label">Weekend Days:</label>
                            <div className="weekend-options">
                                {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                                    <label key={day} className="inline-flex items-center">
                                        <input
                                            type="checkbox"
                                            value={day}
                                            checked={weekendDays.includes(day)}
                                            onChange={handleWeekendChange}
                                            className="inline-input"
                                        />
                                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day]}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="weekend-selection">
                            <label className="textbox-label">Business Hours:</label>
                            <input
                                type="number"
                                max="24"
                                min="1"
                                name="businessHoursPerDay"
                                value={formData.businessHoursPerDay}
                                onChange={handleRestrictedInputChange}
                                className="inline-input input-field input-hours-limited"
                            /> hrs/day
                        </div>
                    </div>

                    {/* Holiday Selection */}
                    <div className="holiday-selection">
                        <label className="holiday-label">Select Holidays:</label>
                        <input
                            type="date"
                            onChange={(e) => handleHolidayChange(e.target.value)}
                            className="inline-input input-date"
                        />
                        <div className="selected-holidays">
                            {holidays.map((date) => (
                                <div key={date} className="holiday-item">
                                    <button
                                        onClick={() => removeHoliday(date)}
                                        className="remove-holiday-button"
                                    >
                                        X
                                    </button>
                                    <span>{date}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <br></br>

                    {/* Resource Allocation Equalizer */}
                    <div>
                        <label className="textbox-label">Resource Allocation Equalizer:</label>
                        <AllocationEqualizer
                            formData={formData}
                            setFormData={setFormData}
                            onAllocationChange={handleAllocationChange}
                        />

                        <ResourceAllocationGrid formData={formData} setFormData={setFormData} highlightedCells={highlightedCells} />
                    </div>
                    <div className="terms-container">
                        {/* Terms & Conditions */}
                        <div className="terms-section">
                            <label className="textbox-label">Terms & Conditions:</label>
                            <RichTextEditor formData={formData} setFormData={setFormData} field="termsAndConditions" placeholder="Enter terms and conditions here..." />
                        </div>

                        {/* Sender & Receiver */}
                        <div className="editor-section">
                            <div className="editor-box">
                                <label className="textbox-label">Prepared By</label>
                                <RichTextEditor formData={formData} setFormData={setFormData} field="estimatePreparedBy" placeholder="Prepared By..." />
                            </div>

                            <div className="editor-box">
                                <label className="textbox-label">Prepared For</label>
                                <RichTextEditor formData={formData} setFormData={setFormData} field="estimatePreparedFor" placeholder="Prepared For..." />
                            </div>
                        </div>
                    </div>


                    {/* Action Buttons */}
                    <div className="button-container">
                        <button onClick={onBackToReview} className="button button-blue">Back to Review</button>
                        <button onClick={handleGenerateResourceAllocation} className="button button-green">Generate Estimate</button>
                        <button onClick={handleDownloadSprintDetailsForJira} className="button button-purple">Export to CSV</button>
                    </div>
                </div>

                <br></br>

                {/* Project Summary Content */}
                {/* <div id="project-summary" className="project-summary-content">
                    <Summary formData={formData} allocations={allocations} />
                    {allocations.map(({ sprint, allocations }, index) => (
                        <SprintCard key={index} sprint={sprint} allocations={allocations} index={index} />
                    ))}
                    <div id="resource-allocation-section">
                    <ResourceAllocationReport formData={formData} setFormData={setFormData} /></div>
                </div> */}

                <div id="project-summary">
                    <div className='right-align'><button onClick={() => openPopup(formData, allocations)} className="text-black underline hover:bg-gray-200 transition bg-transparent border-none px-2 py-1 rounded">Print Preview</button></div>

                    <div id="summary-section">
                        <Summary formData={formData} allocations={allocations} />
                    </div>

                    <div id="sprint-cards-section">
                        {allocations.map(({ sprint, allocations }, index) => (
                            <SprintCard key={index} sprint={sprint} allocations={allocations} index={index} />
                        ))}
                    </div>
                    <div id="resource-allocation-section">
                        <ResourceAllocationReport formData={formData} />
                    </div>

                    {/* <div id="estimated-allocation-section">csv
                        <EstimatedAllocation formData={formData} />
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default ProjectSummary;